import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import {
  Alert,
  Header,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-react';

import CardContainer from '../components/CardContainer';
import HelpLink from '../components/HelpLink';
import ProviderButton from '../components/ProviderButton';
import { useSignInPageSetup } from '../hooks/useSignInPageSetup';
import { useFilteredProviders } from '../hooks/useFilteredProviders';
import useQueryEmailProvider from '../hooks/useQueryEmailProvider';
import alternativeSignInMethodsMessages from '../i18n/alternativeSignInMethods.messages';
import { AppConfig } from '../types/app';
import { IdProvider } from '../types/auth';

import styles from './SignIn.module.css';
import signinMessages from '../i18n/signin.messages';
import { IMPRESSIONS } from '../constants/metrics';
import metricsService from '../services/metrics';
import SignInContainerLayout from '../components/SignInContainerLayout';
import TopHeader from '../components/TopHeader';
import Main from '../components/Main';
import Footer from '../components/Footer';

function AlternativeSignInMethods({
  config,
  providers,
}: {
  config: AppConfig;
  providers: IdProvider[];
}) {
  const { formatMessage } = useIntl();
  const metricsPublisher = useRef(
    metricsService.getPublisher('AlternativeSignInMethodsPage')
  );
  const [essoProvider, setEssoProvider] = useState<IdProvider>();

  const { enableCookieComponent, handleRedirectToIdp, isMobile } =
    useSignInPageSetup(config);
  const { bidProvider } = useFilteredProviders(config, providers);

  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);
  const partnerDomain = queryParameters.get('partnerDomain');

  const { isLoading, error, result, queryEmailDomain } = useQueryEmailProvider(
    config,
    providers
  );

  useEffect(() => {
    metricsPublisher.current.publishCounterMonitor(IMPRESSIONS, 1);
    queryEmailDomain(`ignore@${partnerDomain}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (result?.provider) {
      setEssoProvider(result.provider);
    }
  }, [result]);

  // Do not show any UI while loading.
  if (isLoading) return null;

  const shouldShowBidSeparator = Boolean(bidProvider && essoProvider);
  const hasAlternativeSignInMethods = Boolean(bidProvider || essoProvider);

  return (
    <>
      <TopHeader config={config} />
      <Main config={config}>
        <SignInContainerLayout>
          <br />

          <div className={styles.heading_div}>
            <Header variant="h1">
              {formatMessage(alternativeSignInMethodsMessages.header)}
            </Header>
          </div>

          <div className={styles.heading_div}>
            <Header variant="h3">
              {formatMessage(alternativeSignInMethodsMessages.subHeader)}
            </Header>
          </div>

          <div className={styles.heading_div}>
            <TextContent>
              {formatMessage(alternativeSignInMethodsMessages.linkingInfo)}
            </TextContent>
          </div>

          <CardContainer>
            <SpaceBetween direction="vertical" size="l">
              <div>
                {error || !hasAlternativeSignInMethods ? (
                  <Alert type="warning" data-testid="email-errorAlert">
                    {formatMessage(
                      alternativeSignInMethodsMessages.lookupRequestError
                    )}
                  </Alert>
                ) : (
                  <>
                    {essoProvider && (
                      <ProviderButton
                        provider={essoProvider}
                        key={essoProvider.idp}
                        onClick={handleRedirectToIdp}
                      />
                    )}

                    {shouldShowBidSeparator ? (
                      <h3
                        style={{
                          textAlign: 'center',
                        }}
                        data-testid={'seperator'}
                      >
                        {formatMessage(signinMessages.signInMechanismSeparator)}
                      </h3>
                    ) : null}

                    {bidProvider ? (
                      <ProviderButton
                        provider={bidProvider}
                        key={bidProvider.idp}
                        onClick={handleRedirectToIdp}
                        isMobile={isMobile}
                        alternativeMethod={true}
                      />
                    ) : null}
                  </>
                )}
              </div>
            </SpaceBetween>
          </CardContainer>
          <HelpLink />
        </SignInContainerLayout>
      </Main>
      <Footer enableCookieComponent={enableCookieComponent} />
    </>
  );
}

export default AlternativeSignInMethods;
